import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getImovelPorId } from '../services/imoveisService';
import { useAuth } from '../hooks/useAuth';

const formatBRL = (valor) => {
  const numero = Number(valor || 0);

  if (!Number.isFinite(numero)) return 'Valor não informado';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numero);
};

const getOwnerId = (imovel) => {
  return (
    imovel?.ownerId ||
    imovel?.usuarioId ||
    imovel?.criadoPorUid ||
    imovel?.criadoPor?.uid ||
    imovel?.usuario?.uid ||
    null
  );
};

const getNomeCorretor = (imovel) => {
  return (
    imovel?.corretorContato?.nome ||
    imovel?.ownerNome ||
    imovel?.criadoPor?.nome ||
    imovel?.usuario?.nome ||
    'Corretor responsável'
  );
};

const getTelefoneCorretor = (imovel) => {
  return (
    imovel?.corretorContato?.telefone ||
    imovel?.corretorContato?.whatsapp ||
    imovel?.ownerTelefone ||
    imovel?.criadoPor?.telefone ||
    imovel?.criadoPor?.whatsapp ||
    ''
  );
};

const getEmailCorretor = (imovel) => {
  return (
    imovel?.corretorContato?.email ||
    imovel?.ownerEmail ||
    imovel?.criadoPor?.email ||
    ''
  );
};

const DetalhesImovel = () => {
  const { id } = useParams();
  const { user } = useAuth();

  const [imovel, setImovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const carregarImovel = async () => {
      try {
        setLoading(true);
        setErro('');

        const data = await getImovelPorId(id);
        setImovel(data);
      } catch (error) {
        console.error(error);
        setErro('Erro ao carregar o imóvel.');
      } finally {
        setLoading(false);
      }
    };

    carregarImovel();
  }, [id]);

  const ownerId = useMemo(() => getOwnerId(imovel), [imovel]);

  const ehDono = !!user?.uid && !!ownerId && user.uid === ownerId;

  const ehPonteImobiliaria =
    imovel?.ponteImobiliaria?.ativa === true ||
    imovel?.visibilidadeRede === 'ponte_imobiliaria' ||
    imovel?.motivoVenda === 'migracao';

  const podeVerDadosPrivados = ehDono;

  const podeVerContatoCorretor = ehPonteImobiliaria && !ehDono;

  const ponte = imovel?.ponteImobiliaria || {};

  const cidadeOrigem = ponte?.cidadeOrigem || imovel?.cidade || '';
  const estadoOrigem = ponte?.estadoOrigem || imovel?.estado || '';
  const cidadeDestino = ponte?.cidadeDestino || '';
  const estadoDestino = ponte?.estadoDestino || '';
  const tipoDesejadoDestino = ponte?.tipoDesejadoDestino || '';
  const condicaoNegociacao = ponte?.condicaoNegociacao || '';
  const regraComissao =
    ponte?.regraComissao || imovel?.regraComissao || '50-50';

  const nomeCorretor = getNomeCorretor(imovel);
  const telefoneCorretor = getTelefoneCorretor(imovel);
  const emailCorretor = getEmailCorretor(imovel);

  if (loading) return <p>Carregando imóvel...</p>;
  if (erro) return <p>{erro}</p>;
  if (!imovel) return <p>Imóvel não encontrado.</p>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col gap-2">
          {ehPonteImobiliaria && (
            <span className="w-fit inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
              Ponte Imobiliária
            </span>
          )}

          <h1 className="text-2xl font-bold text-gray-900">
            {imovel.titulo || 'Imóvel sem título'}
          </h1>

          <p className="text-gray-500">
            {imovel.bairro || 'Bairro não informado'} •{' '}
            {imovel.cidade || 'Cidade não informada'} / {imovel.estado || 'UF'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 text-sm text-gray-800">
          <p>
            <strong>Tipo:</strong> {imovel.tipo || 'Não informado'}
          </p>

          <p>
            <strong>Status:</strong> {imovel.status || 'Não informado'}
          </p>

          <p>
            <strong>Preço:</strong> {formatBRL(imovel.preco)}
          </p>

          <p>
            <strong>Bairro:</strong> {imovel.bairro || 'Não informado'}
          </p>

          <p>
            <strong>Cidade:</strong> {imovel.cidade || 'Não informado'} /{' '}
            {imovel.estado || 'UF'}
          </p>

          <p>
            <strong>Dormitórios:</strong> {imovel.dormitorios || 0}
          </p>

          <p>
            <strong>Suítes:</strong> {imovel.suites || 0}
          </p>

          <p>
            <strong>Banheiros:</strong> {imovel.banheiros || 0}
          </p>

          <p>
            <strong>Vagas de garagem:</strong> {imovel.vagas || 0}
          </p>

          <p>
            <strong>Área terreno:</strong> {imovel.areaTerreno || 0} m²
          </p>

          <p>
            <strong>Área construída:</strong> {imovel.areaConstruida || 0} m²
          </p>
        </div>
      </div>

      {ehPonteImobiliaria && (
        <div className="bg-blue-50 rounded-2xl border border-blue-100 p-6">
          <h2 className="text-xl font-bold text-blue-900 mb-3">
            Dados da Ponte Imobiliária
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-950">
            <p>
              <strong>Rota:</strong> {cidadeOrigem || 'Origem não informada'}
              {estadoOrigem ? ` / ${estadoOrigem}` : ''} →{' '}
              {cidadeDestino || 'Destino não informado'}
              {estadoDestino ? ` / ${estadoDestino}` : ''}
            </p>

            <p>
              <strong>Regra de parceria:</strong>{' '}
              {regraComissao === '50-50' ? '50% / 50%' : regraComissao}
            </p>

            <p>
              <strong>Tipo desejado no destino:</strong>{' '}
              {tipoDesejadoDestino || 'Não informado'}
            </p>

            <p>
              <strong>Condição de negociação:</strong>{' '}
              {condicaoNegociacao || 'Não informado'}
            </p>
          </div>

          {ponte?.observacoes && (
            <div className="mt-4 text-sm text-blue-950">
              <strong>Observações da Ponte:</strong>
              <p className="mt-1">{ponte.observacoes}</p>
            </div>
          )}

          <p className="mt-4 text-sm text-blue-800">
            Os dados do proprietário permanecem protegidos. A Ponte Imobiliária
            libera apenas o contato profissional do corretor responsável pela
            angariação.
          </p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-3">
          Descrição do Imóvel
        </h2>

        <p className="text-gray-700 whitespace-pre-line">
          {imovel.descricao || 'Nenhuma descrição informada.'}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-3">Diferenciais</h2>

        {Array.isArray(imovel.diferenciais) &&
        imovel.diferenciais.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {imovel.diferenciais.map((diferencial, i) => (
              <li
                key={`${diferencial}-${i}`}
                className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm"
              >
                {diferencial}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">Nenhum diferencial informado.</p>
        )}
      </div>

      {podeVerDadosPrivados && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Dados Privados
          </h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-800">Endereço Completo</h3>

              <p className="text-gray-700">
                {imovel.rua || 'Rua não informada'}, {imovel.numero || 'S/N'}
              </p>

              <p className="text-gray-700">
                CEP: {imovel.cep || 'Não informado'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800">
                Informações do Proprietário
              </h3>

              <p className="text-gray-700">
                <strong>Nome:</strong>{' '}
                {imovel.proprietarioNome || 'Não informado'}
              </p>

              <p className="text-gray-700">
                <strong>Telefone Principal:</strong>{' '}
                {imovel.proprietarioTelefone || 'Não informado'}
              </p>

              {imovel.proprietarioTelefone2 && (
                <p className="text-gray-700">
                  <strong>Telefone Secundário:</strong>{' '}
                  {imovel.proprietarioTelefone2}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {podeVerContatoCorretor && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Corretor responsável pela angariação
          </h2>

          <p className="text-gray-700">
            Este imóvel faz parte da Ponte Imobiliária. Para avançar na
            oportunidade, fale diretamente com o corretor responsável.
          </p>

          <div className="mt-4 space-y-2 text-sm text-gray-800">
            <p>
              <strong>Nome:</strong> {nomeCorretor}
            </p>

            {imovel?.corretorContato?.creci && (
              <p>
                <strong>CRECI:</strong> {imovel.corretorContato.creci}
              </p>
            )}

            {telefoneCorretor ? (
              <p>
                <strong>WhatsApp:</strong> {telefoneCorretor}
              </p>
            ) : (
              <p className="text-gray-500">
                WhatsApp profissional não informado.
              </p>
            )}

            {emailCorretor && (
              <p>
                <strong>E-mail:</strong> {emailCorretor}
              </p>
            )}
          </div>

          {telefoneCorretor && (
            <a
              href={`https://wa.me/55${String(telefoneCorretor).replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex mt-4 px-4 py-2 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 transition"
            >
              Chamar corretor no WhatsApp
            </a>
          )}
        </div>
      )}

      {!ehDono && !ehPonteImobiliaria && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
          <p className="text-gray-700">
            Este imóvel foi cadastrado por outro corretor.
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Os dados privados do proprietário e o endereço completo permanecem
            protegidos.
          </p>
        </div>
      )}
    </div>
  );
};

export default DetalhesImovel;
